/**
 * TimelineContextMenu Component
 *
 * Context menu for timeline operations:
 * - Create Group: Creates a reusable group from selected tools (requires 2+ selected)
 * - Copy: Copies selected tools to clipboard (requires 1+ selected)
 * - Paste: Pastes tools from clipboard at playhead position (requires clipboard data)
 * - Delete Selected: Deletes all selected items -- tools and placed groups (requires 1+ selected)
 *
 * Uses Radix ContextMenu following the TabContextMenu pattern.
 * Integrates with uiStore for selection state and clipboard.
 * Integrates with timelineStore for group creation, tool deletion, and placed group deletion.
 */

import { useState, useCallback, memo } from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { FolderPlus, Copy, ClipboardPaste, Trash2 } from 'lucide-react';
import { useUiStore, type ClipboardData } from '../../stores/uiStore';
import {
  useTimelineStore,
  calculateRelativeOffsets,
} from '../../stores/timelineStore';
import { GroupNameDialog } from '../dialogs/GroupNameDialog';

interface TimelineContextMenuProps {
  /**
   * Children to render as the context menu trigger
   * Typically the timeline tracks container
   */
  children: React.ReactNode;
}

export const TimelineContextMenu = memo(function TimelineContextMenu({
  children,
}: TimelineContextMenuProps) {
  // Dialog state for group naming
  const [showGroupDialog, setShowGroupDialog] = useState(false);

  // State for menu item visibility - updated when menu opens
  const [menuState, setMenuState] = useState({
    selectedCount: 0,
    hasClipboardData: false,
    isEditingGroup: false,
  });

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      const selectedToolIds = useUiStore.getState().selectedToolIds;
      const clipboard = useUiStore.getState().clipboard;
      const isEditing = useUiStore.getState().editingGroupId !== null;
      const tools = useTimelineStore.getState().tools;
      const placedGroups = useTimelineStore.getState().placedGroups;

      // Count only IDs that correspond to actual tools or placed groups
      let validCount = 0;
      selectedToolIds.forEach((id) => {
        if (
          tools.some((t) => t.id === id) ||
          placedGroups.some((pg) => pg.id === id)
        ) {
          validCount++;
        }
      });

      setMenuState({
        selectedCount: validCount,
        hasClipboardData:
          !isEditing && clipboard !== null && clipboard.tools.length > 0,
        isEditingGroup: isEditing,
      });
    }
  }, []);

  // Derived state for menu items
  const hasSelection = menuState.selectedCount > 0;
  const canCreateGroup =
    menuState.selectedCount >= 2 && !menuState.isEditingGroup;
  const hasClipboardData = menuState.hasClipboardData;

  /**
   * Handle "Create Group" click - opens naming dialog
   */
  const handleCreateGroup = useCallback(() => {
    setShowGroupDialog(true);
  }, []);

  /**
   * Handle group creation from dialog
   * Called when user submits valid group name
   */
  const handleGroupSubmit = useCallback((name: string) => {
    const selectedToolIds = useUiStore.getState().selectedToolIds;
    const createGroup = useTimelineStore.getState().createGroup;
    const clearSelection = useUiStore.getState().clearSelection;

    const selectedIds = Array.from(selectedToolIds);
    const groupId = createGroup(name, selectedIds);

    if (groupId) {
      // Success: close dialog and clear selection
      setShowGroupDialog(false);
      clearSelection();
    }
  }, []);

  /**
   * Handle "Copy" click
   * Copies selected tools to clipboard with relative timing
   */
  const handleCopy = useCallback(() => {
    const selectedToolIds = useUiStore.getState().selectedToolIds;
    const tools = useTimelineStore.getState().tools;
    const copyToClipboard = useUiStore.getState().copyToClipboard;

    // Get selected tools from timeline
    const selectedIds = Array.from(selectedToolIds);
    const selectedTools = tools.filter((t) => selectedIds.includes(t.id));

    if (selectedTools.length === 0) return;

    // Calculate relative offsets for clipboard data
    const clipboardTools = calculateRelativeOffsets(selectedTools);

    const clipboardData: ClipboardData = {
      tools: clipboardTools,
    };

    copyToClipboard(clipboardData);
  }, []);

  /**
   * Handle "Paste" click
   * Pastes tools from clipboard at playhead position
   * Creates new tracks for pasted tools
   */
  const handlePaste = useCallback(() => {
    const clipboard = useUiStore.getState().clipboard;
    const playheadPosition = useTimelineStore.getState().playheadPosition;
    const addTool = useTimelineStore.getState().addTool;
    const addTrack = useTimelineStore.getState().addTrack;

    if (!clipboard || clipboard.tools.length === 0) return;

    // Calculate how many new tracks we need
    const numTracksNeeded = Math.max(
      ...clipboard.tools.map((t) => t.relativeTrackNumber)
    );

    // Get current track count to calculate new track numbers
    const currentTracks = useTimelineStore.getState().tracks;
    const startingTrackNumber = currentTracks.length;

    // Create new tracks for pasting
    const newTrackNumbers: number[] = [];
    for (let i = 0; i < numTracksNeeded; i++) {
      addTrack();
      newTrackNumbers.push(startingTrackNumber + i + 1);
    }

    // Add each tool from clipboard with absolute timing
    clipboard.tools.forEach((clipboardTool) => {
      const absoluteStartTime =
        playheadPosition + clipboardTool.relativeStartTime;
      const targetTrackNumber =
        newTrackNumbers[clipboardTool.relativeTrackNumber - 1];

      addTool({
        toolId: clipboardTool.toolId,
        trackNumber: targetTrackNumber,
        startTime: absoluteStartTime,
        duration: clipboardTool.duration,
        bodyPart: clipboardTool.bodyPart,
        targetRotation: clipboardTool.targetRotation,
        easing: clipboardTool.easing,
        targetPosition: clipboardTool.targetPosition,
      });
    });
  }, []);

  /**
   * Handle "Delete Selected" click
   * Deletes all selected items (tools and placed groups) without confirmation
   */
  const handleDeleteSelected = useCallback(() => {
    const selectedToolIds = useUiStore.getState().selectedToolIds;
    const { removeTool, removePlacedGroup } = useTimelineStore.getState();
    const clearSelection = useUiStore.getState().clearSelection;

    if (selectedToolIds.size === 0) return;

    const selectedIds = Array.from(selectedToolIds);
    const tools = useTimelineStore.getState().tools;
    const placedGroups = useTimelineStore.getState().placedGroups;

    // Partition into tool IDs and placed group IDs, then remove each
    selectedIds.forEach((id) => {
      if (tools.some((t) => t.id === id)) {
        removeTool(id);
      } else if (placedGroups.some((pg) => pg.id === id)) {
        removePlacedGroup(id);
      }
    });

    clearSelection();
  }, []);

  return (
    <>
      <ContextMenu.Root onOpenChange={handleOpenChange}>
        <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>

        <ContextMenu.Portal>
          <ContextMenu.Content
            className="min-w-[180px] rounded-md border border-neutral-700 bg-neutral-800 p-1 shadow-lg"
            style={{ zIndex: 'var(--z-index-dropdown)' }}
          >
            {/* Create Group */}
            <ContextMenu.Item
              onSelect={handleCreateGroup}
              disabled={!canCreateGroup}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-neutral-200 outline-none hover:bg-neutral-700 focus:bg-neutral-700 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent"
            >
              <FolderPlus className="h-4 w-4" />
              Create Group
              {!canCreateGroup && menuState.selectedCount > 0 && (
                <span className="ml-auto text-xs text-neutral-500">
                  (Need 2+)
                </span>
              )}
            </ContextMenu.Item>

            <ContextMenu.Separator className="my-1 h-px bg-neutral-700" />

            {/* Copy */}
            <ContextMenu.Item
              onSelect={handleCopy}
              disabled={!hasSelection}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-neutral-200 outline-none hover:bg-neutral-700 focus:bg-neutral-700 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent"
            >
              <Copy className="h-4 w-4" />
              Copy
              {hasSelection && (
                <span className="ml-auto text-xs text-neutral-400">
                  {menuState.selectedCount} item
                  {menuState.selectedCount !== 1 ? 's' : ''}
                </span>
              )}
            </ContextMenu.Item>

            {/* Paste */}
            <ContextMenu.Item
              onSelect={handlePaste}
              disabled={!hasClipboardData}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-neutral-200 outline-none hover:bg-neutral-700 focus:bg-neutral-700 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent"
            >
              <ClipboardPaste className="h-4 w-4" />
              Paste
            </ContextMenu.Item>

            <ContextMenu.Separator className="my-1 h-px bg-neutral-700" />

            {/* Delete Selected */}
            <ContextMenu.Item
              onSelect={handleDeleteSelected}
              disabled={!hasSelection}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-red-400 outline-none hover:bg-red-900/20 focus:bg-red-900/20 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent"
            >
              <Trash2 className="h-4 w-4" />
              Delete Selected
              {hasSelection && (
                <span className="ml-auto text-xs text-red-400/70">
                  {menuState.selectedCount} item
                  {menuState.selectedCount !== 1 ? 's' : ''}
                </span>
              )}
            </ContextMenu.Item>
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>

      {/* Group Naming Dialog - only mount when needed to prevent unnecessary re-renders */}
      {showGroupDialog && (
        <GroupNameDialog
          open={showGroupDialog}
          onOpenChange={setShowGroupDialog}
          onSubmit={handleGroupSubmit}
        />
      )}
    </>
  );
});
